import React from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Chip,
  Paper
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import HomeIcon from '@mui/icons-material/Home';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useTranslation } from 'react-i18next';

const HierarchyPreview = ({ timelineData }) => {
  const { t } = useTranslation();
  if (!timelineData || !timelineData.phases || timelineData.phases.length === 0) {
    return (
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6" color="text.secondary">
          {t('projectTimelines.noTimelineTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('projectTimelines.noTimelineBody')}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        {t('projectTimelines.projectTimeline')}
      </Typography>
      
      {timelineData.phases.map((phase, phaseIndex) => (
        <Accordion key={phase.id || phaseIndex} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon color="primary" />
              <Typography variant="h6">{phase.name}</Typography>
              <Chip 
                label={`${phase.groups?.length || 0} groups`} 
                size="small" 
                color="primary" 
                variant="outlined"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {phase.groups && phase.groups.length > 0 ? (
              phase.groups.map((group, groupIndex) => (
                <Accordion key={group.id || groupIndex} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GroupWorkIcon color="secondary" />
                      <Typography variant="subtitle1">{group.name}</Typography>
                      <Chip 
                        label={`${group.floors?.length || 0} floors`} 
                        size="small" 
                        color="secondary" 
                        variant="outlined"
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {group.floors && group.floors.length > 0 ? (
                      group.floors.map((floor, floorIndex) => (
                        <Accordion key={floor.id || floorIndex} sx={{ mb: 1 }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <HomeIcon color="info" />
                              <Typography variant="body1">
                                {floor.name}
                                {floor.area && (
                                  <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                                    ({floor.area} m²)
                                  </Typography>
                                )}
                              </Typography>
                              <Chip 
                                label={`${floor.tasks?.length || 0} tasks`} 
                                size="small" 
                                color="info" 
                                variant="outlined"
                              />
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            {floor.tasks && floor.tasks.length > 0 ? (
                              <List dense>
                                {floor.tasks.map((task, taskIndex) => (
                                  <ListItem key={task.id || taskIndex} sx={{ pl: 0 }}>
                                    <AssignmentIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                    <ListItemText
                                      primary={task.name}
                                      secondary={
                                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                          {task.hasWalls && (
                                            <Chip 
                                              label="Walls" 
                                              size="small" 
                                              color="warning" 
                                              variant="outlined"
                                            />
                                          )}
                                          {task.hasDeck && (
                                            <Chip 
                                              label="Deck" 
                                              size="small" 
                                              color="success" 
                                              variant="outlined"
                                            />
                                          )}
                                          {task.duration && (
                                            <Chip 
                                              label={`${task.duration} days`} 
                                              size="small" 
                                              color="default" 
                                              variant="outlined"
                                            />
                                          )}
                                        </Box>
                                      }
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No tasks created yet
                              </Typography>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No floors created yet
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No work groups created yet
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Paper>
  );
};

export default HierarchyPreview;




